import { useState } from "react";

import { Alert, Nav, Tab } from "react-bootstrap";
import { useParams } from "react-router-dom";

import { Loader } from "lucide-react";
import { ConfirmDialog } from "primereact/confirmdialog";

import HistoryTimelineComponent from "@/common/components/HistoryTimelineComponent";
import { Permissions } from "@/common/enums/sharedEnums.ts";
import { CONFIRMATION } from "@/modules/confirmation/routes/confirmationRoutes";
import { OrderNotificationRecipientsFormModal } from "@/modules/order/components/form/OrderNotificationRecipientsFormModal.tsx";
import OrderComments from "@/modules/order/components/OrderComments";
import OrderFilesDataGridComponent from "@/modules/order/components/OrderFilesDataGridComponent";
import OrderInvoiceInfosPreview from "@/modules/order/components/OrderInvoiceInfosPreview";
import OrderKeys from "@/modules/order/components/OrderKeys";
import OrderPreview from "@/modules/order/components/OrderPreview";
import OrderProperties from "@/modules/order/components/OrderProperties";
import OrderStatuses from "@/modules/order/components/OrderStatuses";
import OrderTenants from "@/modules/order/components/OrderTenants";
import { useGetServiceById } from "@/modules/order/hooks/service.hooks";
import { useGetAgencyAdmins } from "@/modules/profile/hooks/agency.hooks.ts";
import history from "@/routes/history";


const OrderContainer = () => {
  const { id: serviceId, tab, mode } = useParams<{ id: string; tab?: string; mode?: string }>();

  const [showModalRecipients, setShowModalRecipients] =
    useState<boolean>(false);
  const currentTab = tab || 'apercu';

  const handleTabSelect = (selectedTab: string | null) => {
    if (selectedTab && selectedTab !== currentTab) {
      const currentMode = mode || 'view';
      const newUrl = `/order/${currentMode}/${serviceId}/${selectedTab}`;
      history.push(newUrl);
    }
  };
  const handleEmailTab = () => {
    setShowModalRecipients(true);
  };

  const {
    error,
    data: service,
    isLoading,
    isFetching,
    refetch,
  } = useGetServiceById(serviceId);

  const { data: agencyAdmins, isLoading: isLoadingAgencyAdmins } =
    useGetAgencyAdmins(`${service?.dwelling.agency.id}`);

  if (isLoading || isLoadingAgencyAdmins) {
    return <Loader />;
  }

  if (error) {
    return (
      <Alert variant="danger">
        Erreur lors du chargement de la commande : {error}
      </Alert>
    );
  }

  if (!service) {
    return (
      <Alert variant="danger">
        Erreur de récupération des données :<br />
        Veuillez réessayer ultérieurement.
      </Alert>
    );
  }

  return (
    <div className="orderPage d-flex flex-column gap-3">
      <ConfirmDialog />
      <OrderNotificationRecipientsFormModal
        serviceId={serviceId}
        show={showModalRecipients}
        onHide={() => setShowModalRecipients(false)}
      />
      <div className="d-flex">
        <h1>Ma commande</h1>
        <a
          href={CONFIRMATION.replace(":id", service.appointment.id)}
          rel="noreferrer"
          target="_blank"
        >
          <i className="bi bi-chat-text" />
        </a>
      </div>
      <div className="bg-container">
        {service.appointment.appointmentStatuses && (
          <OrderStatuses statuses={service.appointment.appointmentStatuses} />
        )}
      </div>
      <div className={"d-flex"}>
        <div className="bg-container w-100">
          <Tab.Container activeKey={currentTab} onSelect={handleTabSelect}>
            <Nav>
              <Nav.Item>
                <Nav.Link eventKey="apercu">Aperçu de la commande</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="properties">Le bien</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="tenantPeople">Les occupants</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="keys">Les clés</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="comments">Les commentaires</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="documents">Les documents</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="invoice">Facture</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link onClick={handleEmailTab}>
                  <i className="bi bi-envelope h5"></i>
                </Nav.Link>
              </Nav.Item>
            </Nav>
            <div className="border-bottom my-3" />
            <div className={"d-flex position-relative"}>
              <Tab.Content style={{ width: "60%" }} className="p-2">
                <Tab.Pane eventKey="apercu">
                  <OrderPreview
                    isAutomaticAssignation={service.isAutomaticAssignation}
                    agencyAdmins={agencyAdmins}
                    createdByUser={service.createdByUser}
                    orderedByUser={service?.orderedByUser}
                    agency={service.dwelling.agency}
                    isManagedByMainGroup={service.isManagedByMainGroup}
                    datedReference={service.datedReference}
                    type={service.type}
                    serviceId={serviceId}
                    operator={service.user}
                    diagnosticTypes={service.diagnosticTypes}
                    operators={service.operators}
                    slug={service.appointment.lastStatus.status.slug}
                    appointment={{
                      startDate: service.appointment.startDate,
                      endDate: service.appointment?.endDate,
                    }}
                    people={service.appointment.people}
                    histories={service.histories}
                    hasSuperAdminPermission={service.permissions.includes(
                      Permissions.SUPER_ADMIN_ACTION
                    )}
                    dwellingId={service.dwelling.id}
                    hasReport={
                      service?.report?.serializedReport &&
                      Object.keys(service?.report?.serializedReport).length > 0
                    }
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="properties">
                  <OrderProperties
                    dwelling={service.dwelling}
                    address={service.dwelling.address}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="tenantPeople">
                  <OrderTenants
                    serviceId={serviceId}
                    tenantPeople={service.appointment?.people}
                    isInsuranceIssuedByAgency={
                      !!service.isInsuranceIssuedByAgency
                    }
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="keys">
                  <OrderKeys
                    data={service.dwelling.key}
                    agency={service.dwelling.agency}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="comments">
                  <OrderComments
                    comments={service.comments}
                    idService={serviceId}
                    canEditComments={service.permissions.includes(
                      Permissions.SERVICE_CREATE
                    )}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="documents">
                  <OrderFilesDataGridComponent
                    serviceId={serviceId}
                    mainFile={service.mainFile}
                    files={service?.report?.files}
                    serviceReportId={service?.report?.id}
                    refetch={refetch}
                    hasUpdatePermission={service.permissions.includes(
                      Permissions.SERVICE_REPORT_UPDATE
                    )}
                    hasSuperAdminPermission={service.permissions.includes(
                      Permissions.SUPER_ADMIN_ACTION
                    )}
                    type={service.type}
                    appointmentStatusSlug={
                      service.appointment.lastStatus.status.slug
                    }
                    externalUrls={service.externalUrls || []}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="invoice">
                  <OrderInvoiceInfosPreview
                    serviceReportId={`${service?.report?.id}`}
                    serviceData={service}
                  />
                </Tab.Pane>
              </Tab.Content>
              <HistoryTimelineComponent
                refetch={refetch}
                isFetching={isFetching}
                histories={service.histories}
              />
            </div>
          </Tab.Container>
        </div>
      </div>
    </div>
  );
};

export default OrderContainer;
